import BatchWorker from './worker?worker';
import type { RasterFormat } from '../core/export/download';
import type { StudioDocument } from '../store/studioStore';
import type { WorkerRequest, WorkerResponse } from './worker';
import type { BatchProgress, BatchStatus, Variant } from './types';

export interface RenderRequest {
  variant: Variant;
  scale: number;
  format: RasterFormat;
  quality: number;
  maxSteps: number;
}

export interface EngineCallbacks {
  onVariant(variant: Variant): void;
  onProgress(progress: BatchProgress): void;
  onFinished(status: BatchStatus): void;
}

/**
 * Worker pool for batch rendering.
 *
 * Design points that matter:
 *
 *  - **Failures are isolated.** A variant that throws is recorded with its message and
 *    the run continues; a single bad parameter combination must not cost a
 *    thousand-image batch.
 *  - **A worker that dies is replaced.** An out-of-memory kill inside one worker
 *    otherwise silently removes a quarter of the throughput for the rest of the run.
 *  - **Pause is real.** It stops dispatching but lets in-flight jobs finish, rather
 *    than terminating workers and losing their font caches.
 */
export class BatchEngine {
  private workers: Worker[] = [];
  private idle: Worker[] = [];
  private queue: RenderRequest[] = [];
  private inFlight = new Map<number, { worker: Worker; request: RenderRequest }>();
  private nextJobId = 1;

  private status: BatchStatus = 'idle';
  private total = 0;
  private completed = 0;
  private failed = 0;
  private startedAt = 0;
  private currentTechnique = '';

  constructor(private readonly callbacks: EngineCallbacks) {}

  get isRunning(): boolean {
    return this.status === 'running' || this.status === 'paused';
  }

  start(requests: RenderRequest[], workerCount: number): void {
    this.stop('idle');

    this.queue = [...requests];
    this.total = requests.length;
    this.completed = 0;
    this.failed = 0;
    this.startedAt = performance.now();
    this.status = 'running';

    const count = Math.max(1, Math.min(workerCount, requests.length));
    for (let i = 0; i < count; i++) this.spawn();

    this.pump();
    this.report();
  }

  private spawn(): Worker {
    const worker = new BatchWorker();
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => this.handle(worker, event.data);
    worker.onerror = () => this.replace(worker, 'Worker abgestürzt.');
    this.workers.push(worker);
    this.idle.push(worker);
    return worker;
  }

  /** Replaces a worker that died, re-queueing whatever it was working on. */
  private replace(dead: Worker, reason: string): void {
    for (const [jobId, entry] of this.inFlight) {
      if (entry.worker !== dead) continue;
      this.inFlight.delete(jobId);
      entry.request.variant.error = reason;
      this.failed++;
      this.callbacks.onVariant(entry.request.variant);
    }
    dead.terminate();
    this.workers = this.workers.filter((w) => w !== dead);
    this.idle = this.idle.filter((w) => w !== dead);
    if (this.status === 'running' || this.status === 'paused') {
      this.spawn();
      this.pump();
    }
    this.report();
  }

  private handle(worker: Worker, message: WorkerResponse): void {
    if (message.type === 'pong') return;

    const entry = this.inFlight.get(message.jobId);
    if (!entry) return;
    this.inFlight.delete(message.jobId);
    this.idle.push(worker);

    const { variant } = entry.request;
    if (message.type === 'done') {
      variant.blob = message.blob;
      variant.url = URL.createObjectURL(message.blob);
      variant.width = message.width;
      variant.height = message.height;
      variant.ms = message.ms;
      variant.elements = message.elements;
      variant.error = null;
      this.completed++;
    } else {
      variant.error = message.error;
      this.failed++;
    }

    this.callbacks.onVariant(variant);
    this.pump();
    this.report();

    if (this.queue.length === 0 && this.inFlight.size === 0 && this.status === 'running') {
      this.finish('done');
    }
  }

  private pump(): void {
    if (this.status !== 'running') return;
    while (this.idle.length && this.queue.length) {
      const worker = this.idle.pop();
      const request = this.queue.shift();
      if (!worker || !request) break;

      const jobId = this.nextJobId++;
      this.inFlight.set(jobId, { worker, request });
      this.currentTechnique = request.variant.meta.techniques.join(' + ') || 'Text';

      const payload: WorkerRequest = {
        type: 'render',
        jobId,
        doc: request.variant.doc,
        scale: request.scale,
        format: request.format,
        quality: request.quality,
        maxSteps: request.maxSteps,
      };
      worker.postMessage(payload);
    }
  }

  pause(): void {
    if (this.status !== 'running') return;
    this.status = 'paused';
    this.report();
  }

  resume(): void {
    if (this.status !== 'paused') return;
    this.status = 'running';
    this.pump();
    this.report();
    if (this.queue.length === 0 && this.inFlight.size === 0) this.finish('done');
  }

  cancel(): void {
    if (!this.isRunning) return;
    this.queue = [];
    this.finish('cancelled');
  }

  private finish(status: BatchStatus): void {
    this.stop(status);
    this.report();
    this.callbacks.onFinished(status);
  }

  private stop(status: BatchStatus): void {
    for (const worker of this.workers) worker.terminate();
    this.workers = [];
    this.idle = [];
    this.inFlight.clear();
    this.queue = [];
    this.status = status;
  }

  dispose(): void {
    this.stop('idle');
  }

  private report(): void {
    const elapsed = this.startedAt ? performance.now() - this.startedAt : 0;
    const finished = this.completed + this.failed;
    // Estimate from the observed throughput rather than a per-variant average, so a
    // batch on eight workers reports eight-way throughput.
    const remaining =
      finished > 0 && this.total > finished ? (elapsed / finished) * (this.total - finished) : null;

    this.callbacks.onProgress({
      status: this.status,
      total: this.total,
      completed: this.completed,
      failed: this.failed,
      elapsed,
      remaining,
      currentTechnique: this.currentTechnique,
    });
  }
}

/** Renders a single document without the pool — used by the gallery's re-export. */
export function renderOne(
  doc: StudioDocument,
  scale: number,
  format: RasterFormat,
  quality: number,
  maxSteps = 40000,
): Promise<{ blob: Blob; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const worker = new BatchWorker();
    const timer = setTimeout(() => {
      worker.terminate();
      reject(new Error('Zeitüberschreitung beim Rendern.'));
    }, 180000);

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      if (message.type === 'pong') return;
      clearTimeout(timer);
      worker.terminate();
      if (message.type === 'done') {
        resolve({ blob: message.blob, width: message.width, height: message.height });
      } else {
        reject(new Error(message.error));
      }
    };
    worker.onerror = () => {
      clearTimeout(timer);
      worker.terminate();
      reject(new Error('Worker abgestürzt.'));
    };

    worker.postMessage({ type: 'render', jobId: 1, doc, scale, format, quality, maxSteps } satisfies WorkerRequest);
  });
}
